/**
 * useLumiAssistant — the headless state machine behind LumiAssistantPanel
 * (ADR 0027 §3.8, ADR 0014 amendment). It owns the conversation client, 5s
 * polling, run approvals, conversation persistence and message parsing — and is
 * kept HEADLESS: it imports nothing app-specific (no axios/pinia/router/i18n).
 * The host app injects an `AssistantTransport`; every network call, navigation
 * and page-context read goes through it, so this file honours every ADR 0014
 * machine-checked fence.
 */
import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';
import type { LumiModuleKey } from '../modules';
import { canShrinkPhoto, shrinkPhoto } from './shrinkPhoto';

export interface AssistantRequestInit {
  method?: 'GET' | 'POST' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | undefined>;
}

export interface AssistantPageContext {
  moduleKey: LumiModuleKey | string;
  route: string;
  entity?: { type: string; id: string } | null;
}

/** A parsed entity-link token `[[module/type:id|label]]`. */
export interface AssistantLinkRef {
  module: string;
  type: string;
  id: string;
  label: string;
}

/**
 * Everything app-specific the panel needs, injected by the host app's ~50-line
 * adapter. `request` paths are relative to the assistant API root
 * (`/api/assistant`); the adapter adds the base URL, auth and `X-Tenant-Slug`.
 */
export interface AssistantTransport {
  request<T = any>(path: string, init?: AssistantRequestInit): Promise<T>;
  navigate(path: string, moduleKey?: string): void;
  context(): AssistantPageContext & { tenantSlug?: string; email?: string; locale?: string };
  /** Resolve an entity-link token to an in-app path; return null to render as text. */
  resolveLink?(ref: AssistantLinkRef): { path: string; moduleKey?: string } | null;
  /** Optional confirm dialog for destructive actions (New session). */
  confirm?(opts: { title: string; body: string }): Promise<boolean>;
  /**
   * Lumi wrote something (#1102): called once per run the panel sees reach
   * `succeeded` after it first read the conversation's runs — an approval, a
   * write the turn ran on its own, a parked run the worker finished. The host
   * tells the page behind the chat, which still shows what it read before.
   */
  runSucceeded?(run: AssistantRunView): void;
}

export interface AssistantMessageView {
  id: string;
  role: string;
  content: string;
  moduleKey?: string | null;
  /**
   * The chat turn this message belongs to, or `null` for a message the turn
   * record does not reach (a module nudge, an approval's settling sentence) or
   * whose turn was swept (issue 534). The panel uses it to place a run's card
   * under the assistant message of the turn that produced the run.
   */
  turnId?: string | null;
  /** When it was written (ISO) — unset on a local echo not read back yet. Places a run without a turn (issue 1074). */
  createdAt?: string;
  /** The person's own verdict on this answer, if they gave one (issue 296, ADR 0071 D5). */
  feedback?: 'up' | 'down' | null;
  feedbackReason?: AssistantFeedbackReason | null;
  /** What the message carried: links, and the files the person attached (ADR 0106 D2). */
  attachmentsJson?: { uploads?: AssistantUploadView[] } | null;
}

/** A file as the conversation knows it — name, type, size; never its bytes (ADR 0106). */
export interface AssistantUploadView {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

/**
 * A file in the composer, before the message goes (ADR 0106 D1). `uploading`
 * while the POST runs, `ready` once assistant-api accepted it (then `id` is the
 * handle the message carries), `error` with the reason the person reads.
 */
export interface AssistantAttachmentDraft {
  localId: string;
  fileName: string;
  sizeBytes: number;
  status: 'uploading' | 'ready' | 'error';
  id?: string;
  error?: AssistantAttachmentError;
}

export type AssistantAttachmentError = 'type' | 'size' | 'tooMany' | 'scan' | 'generic';

/** The four types Lumi takes (ADR 0106 D1) — checked here for a quick answer,
 *  decided by the server from the bytes. */
export const ASSISTANT_UPLOAD_ACCEPT = '.pdf,.docx,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const UPLOAD_EXTENSIONS = /\.(pdf|docx|png|jpe?g)$/i;
export const ASSISTANT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const ASSISTANT_UPLOADS_PER_MESSAGE = 5;

/**
 * The closed reason vocabulary (ADR 0071 D5).
 *
 * Closed because the code — and nothing else — is what crosses into the
 * operator's cross-tenant flag queue (D4: ids and codes, never a sentence).
 * Whatever the person types stays on the message in `assistant-api`.
 */
export const ASSISTANT_FEEDBACK_REASONS = [
  'wrong_result',
  'did_nothing',
  'misunderstood',
  'too_slow',
  'other',
] as const;
export type AssistantFeedbackReason = (typeof ASSISTANT_FEEDBACK_REASONS)[number];

/** Per-message state of the thumbs affordance, keyed on the message id. */
export interface AssistantFeedbackEntry {
  /** Which thumb is showing as chosen — optimistic, so the click feels immediate. */
  value?: 'up' | 'down' | null;
  reason?: AssistantFeedbackReason | null;
  /** True while the reason picker is open for this message. */
  picking?: boolean;
  saving?: boolean;
  failed?: boolean;
  /** Set once the write landed, so the panel can say thank you and stop asking. */
  done?: boolean;
}

export interface AssistantRunView {
  id: string;
  conversationId?: string;
  /** The chat turn this run came out of, or `null` (issue 534) — see `AssistantMessageView.turnId`. */
  turnId?: string | null;
  moduleKey?: string | null;
  toolName?: string | null;
  status: string;
  /** The closed vocabulary (ADR 0071 D2); unset unless the run failed. */
  errorCode?: string | null;
  /** The module's own prose for the failure — what the card shows a person. */
  errorMessage?: string | null;
  /** How the approval gate was answered: `approved | refused | expired`, or `null`. */
  gateOutcome?: string | null;
  /** What the run touched, when known — drives the card's "Öffnen" link. */
  entityType?: string | null;
  entityId?: string | null;
  draftJson?: any;
  argsJson?: any;
  createdAt?: string;
}

/** The structured, module-owned fields a `read_draft` answer may carry (#275, D3). */
export interface AssistantDraftView {
  type?: string;
  candidateName?: string;
  applicationNumber?: string;
  jobTitle?: string;
  /** The module's own prose about the draft (e.g. a job posting's summary,
   *  F7b review pass) — shown alongside the structured fields, never instead
   *  of them. */
  summary?: string;
  subject?: string;
  bodyText?: string | null;
}

/** Per-run state for the fetch-on-open approval card (keyed on `AssistantRun.id`). */
export interface AssistantDraftEntry {
  status: 'loading' | 'loaded' | 'error';
  draft?: AssistantDraftView;
  /** A CODE (`DRAFT_NOT_AVAILABLE`, `DRAFT_NO_MESSAGE`, `DRAFT_NOT_FOUND`,
   *  `permission`, …) — the host maps it to copy in the person's language. */
  reason?: string;
}

/** The approver's in-progress edit of the draft body — text only (#275, D11). */
export interface AssistantDraftEdit {
  bodyText: string;
  dirty: boolean;
}

/** Per-run outcome of the last `approveRun` call (#275, D15). */
export interface AssistantApproveEntry {
  saving: boolean;
  error?: { reason?: string };
}

/** An inline run within a rendered message line: literal text or a deep link. */
export type AssistantLinePart =
  | { kind: 'text'; value: string }
  | { kind: 'link'; text: string; title: string; path: string; moduleKey?: string };

/** A rendered message line as an ordered list of inline parts. */
export interface AssistantLine {
  parts: AssistantLinePart[];
}

// Matches both the namespaced `[[module/type:id|label]]` token and the legacy
// module-less `[[type:id|label]]` one that ATS tools still emit (H3). Global +
// case-insensitive so every link on a line renders, not just the first.
const LINK_RE = /\[\[(?:([a-z0-9_-]+)\/)?([a-z0-9_-]+):([^|\]]+)\|([^\]]+)\]\]/gi;

export function useLumiAssistant(opts: {
  transport: AssistantTransport;
  /** Reactive expanded flag (from the shell state) — gates polling + scroll. */
  expanded: Ref<boolean>;
  /** localStorage key for the active conversationId; scope per tenant+user. */
  conversationStorageKey?: () => string | undefined;
  pollMs?: number;
  labels: { title: string; newSessionTitle: string; newSessionBody: string; sendError: string; actionError: string };
}) {
  const { transport } = opts;
  const pollMs = opts.pollMs ?? 5000;

  const conversationId = ref<string | null>(null);
  // True for this page load only, and only for the person whose Lumi thread was
  // just created: the panel opens itself and Lumi introduces itself once (#419).
  const firstContact = ref(false);
  // True once a thread read has SUCCEEDED — until then an empty `messages` means
  // "not read yet" (or "the read failed"), not "nothing was ever said" (#678): a
  // failed read must not let the greeting latch above a conversation that may
  // have history. A later successful read (the poll) sets it.
  const ready = ref(false);
  const messages = ref<AssistantMessageView[]>([]);
  const runs = ref<AssistantRunView[]>([]);
  // The approval card's fetch-on-open state (#275, D3/D11/D15) — all keyed on
  // the run id, all pruned to the current runs list below.
  const drafts = ref<Record<string, AssistantDraftEntry>>({});
  const edits = ref<Record<string, AssistantDraftEdit>>({});
  const approveState = ref<Record<string, AssistantApproveEntry>>({});
  // Thumbs state, keyed on message id (issue 296, ADR 0071 D5). Optimistic: the
  // person's click shows immediately and the write follows, because a thumb that
  // waits for a round-trip is a thumb people stop pressing — and this is the one
  // signal that catches "no error, but not what I wanted".
  const feedback = ref<Record<string, AssistantFeedbackEntry>>({});
  const feedbackText = ref<Record<string, string>>({});
  const input = ref('');
  // Files waiting in the composer for the next message (ADR 0106).
  const attachments = ref<AssistantAttachmentDraft[]>([]);
  const loading = ref(false);
  const sending = ref(false);
  const statusMessage = ref('');
  // AI disabled / the AI quota used up → hide the composer, show one sentence.
  // The sentence is assistant-api's, in the reader's language: for the quota
  // (issue 665) it says the quota for this billing period is spent, that a billing
  // manager can add an AI quota upgrade under Billing, and the reset date when
  // master knows it. This file stays headless and holds no copy of its own.
  const composerDisabled = ref(false);
  const disabledNotice = ref('');

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let tempCounter = 0;
  let lastMsgSig = '';
  let lastRunSig = '';
  let inFlight = false;

  function storageKey(): string | undefined {
    return opts.conversationStorageKey?.();
  }
  function persistConversation(id: string | null) {
    const key = storageKey();
    if (!key || typeof localStorage === 'undefined') return;
    try {
      if (id) localStorage.setItem(key, id);
      else localStorage.removeItem(key);
    } catch {
      /* quota / denied — non-fatal */
    }
  }
  function readStoredConversation(): string | null {
    const key = storageKey();
    if (!key || typeof localStorage === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  function msgSig(list: AssistantMessageView[]): string {
    return (
      `${list.length}|` +
      list.slice(-25).map((m) => `${m.id}:${m.role}:${m.content.length}:${m.turnId ?? ''}`).join('|')
    );
  }
  function runSig(list: AssistantRunView[]): string {
    return list.map((r) => `${r.id}:${r.status}:${r.turnId ?? ''}:${r.gateOutcome ?? ''}`).join('|');
  }
  // The status each run had when this panel last read it (#1102), or `null`
  // until the first read of the conversation's runs: what was already settled
  // when the panel opened is history, not news, and must not reload a page.
  // A thread this panel starts afresh (New session) has no history, so it
  // begins primed and its first write is reported.
  let seenRunStatus: Map<string, string> | null = null;
  function noteSucceededRuns(list: AssistantRunView[]): void {
    if (seenRunStatus === null) {
      seenRunStatus = new Map(list.map((r) => [r.id, r.status]));
      return;
    }
    for (const r of list) {
      const before = seenRunStatus.get(r.id);
      seenRunStatus.set(r.id, r.status);
      if (r.status !== 'succeeded' || before === 'succeeded') continue;
      try {
        transport.runSucceeded?.(r);
      } catch {
        /* the page's refresh is a courtesy — it never breaks the panel */
      }
    }
  }
  /** Replace `runs` iff the list actually changed — shared by every source that
   *  can hand back a run list (`conversations/:id` and `runs`, issue 534). */
  function applyRuns(list: AssistantRunView[]): void {
    noteSucceededRuns(list);
    const sig = runSig(list);
    if (sig !== lastRunSig) {
      lastRunSig = sig;
      runs.value = list;
    }
  }

  // Parse `[[module/type:id|label]]` (and legacy module-less) tokens into ordered
  // inline parts, so multiple links on one line all render. A link the app can't
  // resolve (unknown module/type) falls back to its plain label text. Legacy
  // tokens carry no module — the app's resolveLink infers it from the type.
  function parseLines(content: string): AssistantLine[] {
    return content.split('\n').map((line) => {
      const parts: AssistantLinePart[] = [];
      let lastIndex = 0;
      LINK_RE.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = LINK_RE.exec(line)) !== null) {
        if (m.index > lastIndex) parts.push({ kind: 'text', value: line.slice(lastIndex, m.index) });
        const ref: AssistantLinkRef = { module: m[1] || '', type: m[2], id: m[3], label: m[4].trim() };
        const resolved = transport.resolveLink?.(ref) || null;
        if (resolved) {
          parts.push({ kind: 'link', text: ref.label, title: ref.label, path: resolved.path, moduleKey: resolved.moduleKey });
        } else {
          parts.push({ kind: 'text', value: ref.label });
        }
        lastIndex = m.index + m[0].length;
      }
      if (lastIndex < line.length) parts.push({ kind: 'text', value: line.slice(lastIndex) });
      if (parts.length === 0) parts.push({ kind: 'text', value: '' });
      return { parts };
    });
  }

  async function loadConversation(showLoading = false) {
    if (!conversationId.value) return;
    if (showLoading) loading.value = true;
    try {
      const res = await transport.request<{ messages?: AssistantMessageView[]; runs?: AssistantRunView[] }>(
        `conversations/${conversationId.value}`,
      );
      const next = res?.messages || [];
      const sig = msgSig(next);
      if (sig !== lastMsgSig) {
        lastMsgSig = sig;
        messages.value = next;
      }
      ready.value = true;
      // Rehydrate the thumbs from the server, so a reload (or a module switch,
      // which is a full page load) shows the one the person already pressed.
      // A picker the person has open right now is left alone: the poll must not
      // close a form mid-typing.
      hydrateFeedback(next);
      // `conversations/:id` carries `runs` alongside `messages` (issue 534) —
      // the same authoritative read, so a card is never placed against a
      // message id the runs snapshot does not (yet) agree exists.
      if (res?.runs) applyRuns(res.runs);
    } catch (e: any) {
      const status = e?.status || e?.response?.status;
      if (status === 400 || status === 404) {
        conversationId.value = null;
        persistConversation(null);
        messages.value = [];
        lastMsgSig = '';
        // Whatever thread comes next is read afresh: its settled runs are history.
        seenRunStatus = null;
      }
    } finally {
      if (showLoading) loading.value = false;
    }
  }

  async function loadRuns() {
    if (!conversationId.value) return;
    try {
      const res = await transport.request<AssistantRunView[]>('runs', { query: { conversationId: conversationId.value } });
      applyRuns(Array.isArray(res) ? res : []);
    } catch {
      /* ignore — conversation may have been deleted */
    }
  }

  async function pollOnce() {
    if (inFlight || !opts.expanded.value || !conversationId.value) return;
    inFlight = true;
    try {
      await loadRuns();
      await loadConversation();
    } catch {
      /* ignore */
    } finally {
      inFlight = false;
    }
  }

  /** A file the person dropped or picked: checked, then uploaded on its own. */
  async function addFiles(files: ArrayLike<File>) {
    for (const file of Array.from(files)) {
      const draft: AssistantAttachmentDraft = {
        localId: `att-${tempCounter++}`,
        fileName: file.name,
        sizeBytes: file.size,
        status: 'uploading',
      };
      const open = attachments.value.filter((a) => a.status !== 'error').length;
      if (open >= ASSISTANT_UPLOADS_PER_MESSAGE) {
        attachments.value = [...attachments.value, { ...draft, status: 'error', error: 'tooMany' }];
        continue;
      }
      if (!UPLOAD_EXTENSIONS.test(file.name)) {
        attachments.value = [...attachments.value, { ...draft, status: 'error', error: 'type' }];
        continue;
      }
      // A large phone photo is made smaller on its way up (shrinkPhoto.ts), so
      // only a file that cannot be is refused for its size here.
      if (file.size > ASSISTANT_UPLOAD_MAX_BYTES && !canShrinkPhoto(file)) {
        attachments.value = [...attachments.value, { ...draft, status: 'error', error: 'size' }];
        continue;
      }
      attachments.value = [...attachments.value, draft];
      void upload(draft.localId, file);
    }
  }

  async function upload(localId: string, file: File) {
    const patch = (next: Partial<AssistantAttachmentDraft>) => {
      attachments.value = attachments.value.map((a) => (a.localId === localId ? { ...a, ...next } : a));
    };
    try {
      const sendable = await shrinkPhoto(file);
      if (sendable.size > ASSISTANT_UPLOAD_MAX_BYTES) return patch({ status: 'error', error: 'size' });
      if (sendable !== file) patch({ sizeBytes: sendable.size });
      const form = new FormData();
      form.append('file', sendable, sendable.name);
      const res = await transport.request<AssistantUploadView>('uploads', { method: 'POST', body: form });
      // Removed while it was uploading: the file goes again.
      if (!attachments.value.some((a) => a.localId === localId)) {
        if (res?.id) void transport.request(`uploads/${res.id}`, { method: 'DELETE' }).catch(() => undefined);
        return;
      }
      if (!res?.id) return patch({ status: 'error', error: 'generic' });
      patch({ status: 'ready', id: res.id, fileName: res.fileName || file.name });
    } catch (err: any) {
      const code = err?.response?.data?.code ?? err?.response?.data?.message?.code;
      const error: AssistantAttachmentError =
        code === 'UPLOAD_TYPE_REFUSED'
          ? 'type'
          : code === 'UPLOAD_TOO_LARGE' || err?.response?.status === 413
            ? 'size'
            : code === 'UPLOAD_QUARANTINED'
              ? 'scan'
              : 'generic';
      patch({ status: 'error', error });
    }
  }

  function removeAttachment(localId: string) {
    const gone = attachments.value.find((a) => a.localId === localId);
    attachments.value = attachments.value.filter((a) => a.localId !== localId);
    if (gone?.status === 'ready' && gone.id) {
      void transport.request(`uploads/${gone.id}`, { method: 'DELETE' }).catch(() => undefined);
    }
  }

  async function send() {
    const text = input.value.trim();
    // A file still uploading holds the message back: it would go without it.
    if (sending.value || attachments.value.some((a) => a.status === 'uploading')) return;
    const ready = attachments.value.filter((a) => a.status === 'ready' && a.id);
    if (!text && ready.length === 0) return;
    const uploadIds = ready.map((a) => a.id as string);
    const sentAttachments = attachments.value;
    // Cleared THE MOMENT the send starts, not once the server loop returns
    // (issue 534): a chat turn can run for a while (CHAT_TIMEOUT_MS), and a box
    // still holding the sent sentence for that whole time reads as "not sent
    // yet". Kept in `text` for the one case that needs it back — a failed send.
    input.value = '';
    attachments.value = [];
    sending.value = true;
    statusMessage.value = '';
    messages.value.push({
      id: `local-${tempCounter++}`,
      role: 'user',
      content: text,
      ...(ready.length
        ? { attachmentsJson: { uploads: ready.map((a) => ({ id: a.id as string, fileName: a.fileName, mimeType: '', sizeBytes: a.sizeBytes })) } }
        : {}),
    });
    try {
      const page = transport.context();
      const res = await transport.request<{
        conversationId?: string;
        /** The turn's answer — for `aiDisabled`/`tokenLimit`, the refusal sentence. */
        response?: string;
        navigate?: { path: string; moduleKey?: string };
        aiDisabled?: boolean;
        tokenLimit?: boolean;
        /** With `tokenLimit`: when the AI quota resets (ISO), already named in `response`. */
        resetsAt?: string | null;
      }>('chat', {
        method: 'POST',
        body: {
          message: text,
          conversationId: conversationId.value || undefined,
          ...(uploadIds.length ? { uploadIds } : {}),
          pageContext: { moduleKey: page.moduleKey, route: page.route, entityType: page.entity?.type ?? null, entityId: page.entity?.id ?? null },
        },
      });
      if (res?.conversationId && res.conversationId !== conversationId.value) {
        conversationId.value = res.conversationId;
        persistConversation(res.conversationId);
      }
      // The one authoritative read after a send (issue 534): ids, turnIds and
      // runs together, from the server — not the locally pushed `local-N` echo
      // and a separately-fetched run list that could disagree with it.
      await loadConversation(true);
      if (res?.aiDisabled || res?.tokenLimit) {
        composerDisabled.value = true;
        // The refusal this turn returned, not whatever the thread's last message
        // happens to be after the re-read: the notice is the one place the reader
        // learns where to go (Billing) and when the quota resets (issue 665).
        disabledNotice.value = res.response || messages.value[messages.value.length - 1]?.content || '';
      }
      if (res?.navigate?.path) transport.navigate(res.navigate.path, res.navigate.moduleKey);
    } catch {
      // Put the sentence back — but only if the box is still the empty one
      // `send()` left it in. Anything the person typed while waiting is theirs,
      // not a place to splice a retry into.
      if (!input.value.trim()) input.value = text;
      // The files come back too, unless the person already started a new set.
      if (attachments.value.length === 0) attachments.value = sentAttachments;
      statusMessage.value = opts.labels.sendError;
    } finally {
      sending.value = false;
    }
  }

  // What the approver is being asked to approve (#275, D3). Fetched once per
  // run, through assistant-api, when the card first shows a `needs_approval`
  // run — never re-fetched on a later poll of the same run, and never called
  // for a run whose id is already in `drafts` (loading, loaded or errored: a
  // failed read is not retried automatically, the same one-shot policy the
  // read side documents).
  async function loadDraft(id: string) {
    if (drafts.value[id]) return;
    drafts.value = { ...drafts.value, [id]: { status: 'loading' } };
    try {
      const res = await transport.request<
        { kind: 'draft'; draft: AssistantDraftView } | { kind: 'error'; reason: string }
      >(`runs/${id}/draft`);
      if (res && res.kind === 'draft') {
        drafts.value = { ...drafts.value, [id]: { status: 'loaded', draft: res.draft } };
        edits.value = { ...edits.value, [id]: { bodyText: res.draft.bodyText ?? '', dirty: false } };
      } else {
        drafts.value = { ...drafts.value, [id]: { status: 'error', reason: (res && res.reason) || 'DRAFT_READ_FAILED' } };
      }
    } catch {
      drafts.value = { ...drafts.value, [id]: { status: 'error', reason: 'DRAFT_READ_FAILED' } };
    }
  }

  // The approver's edit of the body — TEXT only (#275, D11): there is no
  // second body to edit, so the panel never offers one.
  function editDraftBody(id: string, value: string) {
    const entry = drafts.value[id];
    const original = entry?.status === 'loaded' ? entry.draft?.bodyText ?? '' : '';
    edits.value = { ...edits.value, [id]: { bodyText: value, dirty: value !== original } };
  }

  async function approveRun(id: string) {
    const edit = edits.value[id];
    const entry = drafts.value[id];
    // Only sent when the person actually edited (#275, D11) — an untouched
    // draft rides the commit exactly as approveRun always has.
    const editedPayload =
      edit?.dirty && entry?.status === 'loaded' ? { bodyText: edit.bodyText } : undefined;
    approveState.value = { ...approveState.value, [id]: { saving: true } };
    try {
      await transport.request(`runs/${id}/approve`, {
        method: 'POST',
        ...(editedPayload ? { body: { editedPayload } } : {}),
      });
      approveState.value = { ...approveState.value, [id]: { saving: false } };
      await loadRuns();
    } catch (e: any) {
      // Approve failures stop being swallowed (#275, D15). Most module-side
      // refusals already reach the person as the run flipping to `failed` on
      // the next poll — this catches what does NOT: assistant-api's own
      // claim-time refusals (already claimed, not awaiting approval, run
      // gone) and any transport failure, none of which flip the run at all.
      // A 403 keeps its `reason` (assistant-tools.v1 §2); anything else gets
      // copy that is true without inventing a specific cause (see the S7
      // brief's "one more thing" — the module error channel is not widened
      // here).
      const status = e?.status || e?.response?.status;
      const reason = status === 403 ? e?.response?.data?.reason || e?.data?.reason || 'permission' : undefined;
      approveState.value = { ...approveState.value, [id]: { saving: false, error: { reason } } };
    }
  }
  /**
   * Take the thumbs state from the server for messages the person is not
   * currently answering about.
   */
  function hydrateFeedback(list: AssistantMessageView[]): void {
    const next = { ...feedback.value };
    for (const message of list) {
      if (!message.feedback) continue;
      const current = next[message.id];
      if (current?.picking || current?.saving) continue;
      next[message.id] = { value: message.feedback, reason: message.feedbackReason ?? null, done: true };
    }
    feedback.value = next;
  }

  /**
   * One click of the thumb (issue 296, ADR 0071 D5, PDR 0023 rule 4).
   *
   * A thumbs-UP is final and posts straight away — there is nothing to ask. A
   * thumbs-DOWN opens the reason picker instead of posting, because the reason
   * CODE is the whole value of the signal downstream: a bare "this was bad" in
   * the operator's queue names no problem to fix.
   */
  function rateMessage(id: string, value: 'up' | 'down'): void {
    const entry = { ...(feedback.value[id] || {}), value, failed: false };
    if (value === 'down') {
      feedback.value = { ...feedback.value, [id]: { ...entry, picking: true, done: false } };
      return;
    }
    feedback.value = { ...feedback.value, [id]: { ...entry, picking: false, reason: null } };
    void postFeedback(id, 'up', null, null);
  }

  function setFeedbackText(id: string, text: string): void {
    feedbackText.value = { ...feedbackText.value, [id]: text };
  }

  /** Send the thumbs-down with the reason the person picked. */
  function submitFeedbackReason(id: string, reason: AssistantFeedbackReason): void {
    feedback.value = { ...feedback.value, [id]: { ...(feedback.value[id] || {}), value: 'down', reason, picking: false } };
    void postFeedback(id, 'down', reason, feedbackText.value[id] || null);
  }

  /** Close the picker without saying anything — a thumb pressed by accident. */
  function dismissFeedback(id: string): void {
    const next = { ...feedback.value };
    delete next[id];
    feedback.value = next;
    const text = { ...feedbackText.value };
    delete text[id];
    feedbackText.value = text;
  }

  async function postFeedback(
    id: string,
    value: 'up' | 'down',
    reason: AssistantFeedbackReason | null,
    text: string | null,
  ): Promise<void> {
    feedback.value = { ...feedback.value, [id]: { ...(feedback.value[id] || {}), saving: true, failed: false } };
    try {
      await transport.request(`messages/${id}/feedback`, {
        method: 'POST',
        body: { value, ...(reason ? { reason } : {}), ...(text ? { text } : {}) },
      });
      feedback.value = { ...feedback.value, [id]: { value, reason, saving: false, done: true } };
    } catch {
      // Reported in place, on the message it belongs to — never a toast
      // (ADR 0068 rule 2's inline tier). The thumb goes back to unpressed so a
      // failed write does not look like a recorded one.
      feedback.value = { ...feedback.value, [id]: { value: null, reason: null, saving: false, failed: true } };
    }
  }

  async function cancelRun(id: string) {
    statusMessage.value = '';
    try {
      await transport.request(`runs/${id}/cancel`, { method: 'POST' });
      await loadRuns();
    } catch {
      statusMessage.value = opts.labels.actionError;
    }
  }

  async function clearSession() {
    if (conversationId.value || messages.value.length) {
      const ok = transport.confirm
        ? await transport.confirm({ title: opts.labels.newSessionTitle, body: opts.labels.newSessionBody })
        : true;
      if (!ok) return;
    }
    if (conversationId.value) {
      try {
        await transport.request(`conversations/${conversationId.value}`, { method: 'DELETE' });
      } catch {
        // The conversation is dropped locally whatever the server said, so the user's
        // intent succeeds either way; a failure here leaves an orphaned row server-side.
      }
    }
    conversationId.value = null;
    persistConversation(null);
    // The singleton thread row is the server's only record that this person has
    // met Lumi (#419). Deleting it would make their NEXT sign-in a "first contact"
    // again — the panel popping open with the long introduction (issue 718) — so
    // the thread is minted afresh right here, and the `created` it reports is
    // deliberately discarded: the person has met Lumi.
    try {
      const res = await transport.request<{ conversationId?: string }>('default-conversation');
      conversationId.value = res?.conversationId || null;
      persistConversation(conversationId.value);
    } catch {
      /* a failed re-mint falls back to the old behaviour: minted on the first message */
    }
    firstContact.value = false;
    messages.value = [];
    runs.value = [];
    drafts.value = {};
    edits.value = {};
    approveState.value = {};
    feedback.value = {};
    feedbackText.value = {};
    input.value = '';
    statusMessage.value = '';
    composerDisabled.value = false;
    disabledNotice.value = '';
    lastMsgSig = '';
    lastRunSig = '';
    seenRunStatus = new Map();
  }

  // Boot: prefer the persisted conversationId (validated), else the server default.
  async function boot() {
    const stored = readStoredConversation();
    if (stored) {
      conversationId.value = stored;
      await loadConversation(true);
      // loadConversation clears the id on 400/404; if it survived, we're done.
      if (conversationId.value) {
        await loadRuns();
        return;
      }
    }
    try {
      const res = await transport.request<{ conversationId?: string; created?: boolean }>('default-conversation');
      conversationId.value = res?.conversationId || null;
      // The one call that MINTED this person's thread is their first contact with
      // Lumi (#419) — a fact the server owns, because a browser flag would greet
      // the same person again on every new device. Read defensively: an
      // assistant-api that predates the field simply never greets.
      firstContact.value = res?.created === true;
      persistConversation(conversationId.value);
      await loadConversation(true);
      await loadRuns();
    } catch {
      /* panel stays usable after the first message */
    }
  }

  // Send an external text (the `lumi:message` bridge) as if typed.
  async function sendExternal(text: string) {
    input.value = text;
    await send();
  }

  onMounted(() => {
    void boot();
    pollTimer = setInterval(() => void pollOnce(), pollMs);
  });
  onBeforeUnmount(() => {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
  });

  // When the panel re-expands, pull fresh state immediately (don't wait a tick).
  watch(
    () => opts.expanded.value,
    (open) => {
      if (open) void pollOnce();
    },
  );

  // "The card fetches the draft when it opens" (#275, D3): a `needs_approval`
  // run showing up in the list IS the card opening, so this fires the fetch —
  // once per run id, never re-triggered by a later poll of the same run — and
  // prunes the per-run maps down to the runs still on the list, so an
  // approved/cancelled/gone run's state does not linger.
  watch(runs, (list) => {
    const ids = new Set(list.map((r) => r.id));
    const prune = <T,>(map: Record<string, T>): Record<string, T> => {
      const next: Record<string, T> = {};
      for (const [k, v] of Object.entries(map)) if (ids.has(k)) next[k] = v;
      return next;
    };
    drafts.value = prune(drafts.value);
    edits.value = prune(edits.value);
    approveState.value = prune(approveState.value);
    for (const r of list) {
      if (r.status === 'needs_approval' && !drafts.value[r.id]) void loadDraft(r.id);
    }
  });

  return {
    conversationId,
    firstContact,
    ready,
    messages,
    runs,
    drafts,
    edits,
    approveState,
    feedback,
    feedbackText,
    rateMessage,
    setFeedbackText,
    submitFeedbackReason,
    dismissFeedback,
    editDraftBody,
    input,
    attachments,
    addFiles,
    removeAttachment,
    loading,
    sending,
    statusMessage,
    composerDisabled,
    disabledNotice,
    parseLines,
    send,
    sendExternal,
    approveRun,
    cancelRun,
    clearSession,
  };
}
